import {
    CalendarToday as DateIcon,
    Storage as SizeIcon,
    VideoFile as VideoIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Grid,
    Paper,
    Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FileItem, queryFileItem, updateFileItem } from "../Api";
import Editable from "../basic/Editable";
import { useLocalStorage } from "../hooks/UseLocalStorage";

export const Video: React.FC = () => {
    const { key } = useParams();
    const [fileItem, setFileItem] = useState<FileItem>();
    const [callLocal] = useLocalStorage("callLocal", false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const videoElement = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (key) {
            setLoading(true);
            setError(null);
            queryFileItem(callLocal, key)
                .then((value) => {
                    if (value) {
                        setFileItem(value);
                    } else {
                        setError("Video not found");
                    }
                    setLoading(false);
                })
                .catch((err) => {
                    setError("Failed to load video");
                    setLoading(false);
                    console.error(err);
                });
        }
    }, [key, callLocal]);

    async function setSummary(newSummary: string): Promise<void> {
        if (fileItem) {
            const newFileItem: FileItem = { ...fileItem, summary: newSummary };
            await updateAndReload(newFileItem);
        }
    }

    async function setTitle(newTitle: string): Promise<void> {
        if (fileItem) {
            const newFileItem: FileItem = { ...fileItem, title: newTitle };
            await updateAndReload(newFileItem);
        }
    }

    async function setDate(newDate: string): Promise<void> {
        if (fileItem) {
            const newFileItem: FileItem = { ...fileItem, date: newDate };
            await updateAndReload(newFileItem);
        }
    }

    async function updateAndReload(newFileItem: FileItem): Promise<void> {
        await updateFileItem(callLocal, newFileItem);
        const updatedFileItem = await queryFileItem(callLocal, newFileItem.key);
        if (updatedFileItem) {
            console.log('aftersafe', updatedFileItem);
            setFileItem(updatedFileItem);
        }
    }

    function setTime(time: number): void {
        if (videoElement.current) {
            videoElement.current.currentTime = time;
        }
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 3 }}>
                {error}
            </Alert>
        );
    }

    if (!fileItem) {
        return (
            <Alert severity="warning" sx={{ mb: 3 }}>
                Video not found
            </Alert>
        );
    }

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <VideoIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Typography variant="h3" component="h1">
                        Video Player
                    </Typography>
                </Box>
                
                {/* Video Info */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Typography variant="h5" gutterBottom>
                                <Editable value={fileItem.title} setValue={setTitle} />
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                <Chip
                                    icon={<DateIcon />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>Date:</span>
                                            <Editable value={fileItem.date} setValue={setDate} />
                                        </Box>
                                    }
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<SizeIcon />}
                                    label={`Size: ${fileItem.size}`}
                                    variant="outlined"
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Filename:</strong> {fileItem.filename}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            {/* Video Player */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Box sx={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0 }}>
                    <video
                        ref={videoElement}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            borderRadius: '8px',
                        }}
                        src={fileItem.signedUrl}
                        controls={true}
                        autoPlay={true}
                    />
                </Box>
            </Paper>

            {/* Summary Section */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Summary & Notes
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Editable
                        multiLine={true}
                        value={fileItem.summary}
                        setValue={setSummary}
                        setTime={setTime}
                    />
                </Box>
            </Paper>
        </Box>
    );
};
